import { format, isSameDay } from 'date-fns';
import { Pressable, View, useWindowDimensions } from 'react-native';
import { Text } from '@/components/ui/text';
import { LabeledSection } from '~/layout/components/labeled-section/LabeledSection';
import { LayoutBreakpoints } from '@/constants/LayoutBreakpoints';
import { cn } from '@/lib/utils';

// Mobile vs desktop day variants are rendered conditionally instead of toggled
// with `display: none` (via `hidden`/`md:block`): `display: none` on a
// re-rendering node triggers a New-Architecture Yoga crash (YGNodeGetOwner
// assert, RN #52349), so the hidden variant must not be in the tree at all.

export interface WeekSelectorProps {
  selectedDate: Date;
  weekDates: Date[];
  onDateSelect?: (date: Date) => void;
  workoutDates?: string[];
  className?: string;
}

interface DayColumnProps {
  date: Date;
  isSelected: boolean;
  hasWorkout: boolean;
  isDesktop: boolean;
  onPress: () => void;
}

const getDayLetter = (date: Date): string => {
  return format(date, 'EEEEEE')[0];
};

const DayColumn = ({
  date,
  isSelected,
  hasWorkout,
  isDesktop,
  onPress,
}: DayColumnProps) => {
  const dayLetter = getDayLetter(date);
  const dateNum = format(date, 'd');

  return (
    <Pressable
      onPress={onPress}
      className={cn(
        'items-center flex-1',
        'md:rounded-lg md:gap-1.5',
        isSelected
          ? 'md:bg-accent md:hover:brightness-95'
          : 'md:hover:bg-surface-elevated',
        'gap-2',
      )}
      style={{ paddingVertical: 12, paddingHorizontal: 8 }}
    >
      <Text
        className={cn(
          'font-inter',
          'text-[10px] md:text-[11px]',
          isSelected
            ? 'text-accent md:text-text-primary font-inter-bold'
            : 'text-text-muted font-inter-semibold',
        )}
      >
        {dayLetter}
      </Text>

      {/* Desktop: plain date number */}
      {isDesktop && (
        <Text className="text-[16px] font-sora-semibold text-text-primary">
          {dateNum}
        </Text>
      )}

      {/* Mobile: selected day shows the number inside an accent circle */}
      {!isDesktop && isSelected && (
        <View className="w-7 h-7 rounded-full bg-accent items-center justify-center">
          <Text className="text-[10px] font-inter-bold text-accent-foreground">
            {dateNum}
          </Text>
        </View>
      )}

      {/* Workout indicator dot (variant per breakpoint) */}
      {hasWorkout && !isDesktop && !isSelected && (
        <View className="w-2 h-2 rounded-full bg-accent" />
      )}
      {hasWorkout && isDesktop && (
        <View
          className={cn(
            'rounded-full w-1.5 h-1.5',
            isSelected ? 'bg-text-primary' : 'bg-accent',
          )}
        />
      )}
    </Pressable>
  );
};

export const WeekSelector = ({
  selectedDate,
  weekDates,
  onDateSelect,
  workoutDates,
  className,
}: WeekSelectorProps) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= LayoutBreakpoints.tablet;

  return (
    <View className={cn('w-full flex-col gap-3', className)}>
      <LabeledSection.Label>THIS WEEK</LabeledSection.Label>

      <View
        className={cn(
          'flex-row rounded-xl bg-surface',
          'p-3 md:p-4',
          'justify-between md:gap-2',
        )}
      >
        {weekDates.map((date, index) => {
          const dateKey = format(date, 'yyyy-MM-dd');
          const isSelected = isSameDay(date, selectedDate);
          const hasWorkout = workoutDates?.includes(dateKey) ?? false;

          return (
            <DayColumn
              key={index}
              date={date}
              isSelected={isSelected}
              hasWorkout={hasWorkout}
              isDesktop={isDesktop}
              onPress={() => onDateSelect?.(date)}
            />
          );
        })}
      </View>
    </View>
  );
};
